import { Link } from "@/lib/router-compat";
import { ChevronRight, MapPin, Sparkles } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import { ServiceItem } from "@/config/navigation";
import { useState } from "react";

interface ServiceMenuProps {
  trigger: string;
  services: ServiceItem[];
}

export function ServiceMenu({ trigger, services }: ServiceMenuProps) {
  const [hovered, setHovered] = useState<ServiceItem | null>(null);

  const hasSubItems = (s?: ServiceItem) => !!s && Array.isArray(s.subItems) && s.subItems.length > 0;
  const menuHasSubItems = services.some((s) => hasSubItems(s));

  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="h-auto bg-transparent px-0 py-0 text-sm font-medium tracking-tight text-muted-foreground transition-colors duration-200 hover:bg-transparent hover:text-foreground focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-foreground">
            {trigger}
          </NavigationMenuTrigger>

          <NavigationMenuContent
            className={`z-[100] overflow-visible rounded-xl border border-border/80 bg-card/95 shadow-2xl shadow-background/60 backdrop-blur-xl ${
              menuHasSubItems ? "md:min-w-[680px]" : "md:min-w-[340px]"
            }`}
          >
            <div className={`grid ${menuHasSubItems ? "grid-cols-1 md:grid-cols-[1.15fr_0.85fr]" : "grid-cols-1"}`}>
              {/* Columna izquierda: servicios */}
              <div className="p-3">
                <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  {trigger}
                </p>
                <ul className="space-y-0.5">
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <li key={service.path} onMouseEnter={() => setHovered(service)}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={service.path}
                            className="group flex items-start gap-3 rounded-lg p-3 transition-colors duration-200 hover:bg-primary/10"
                          >
                            {Icon && (
                              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-background/60 text-primary transition-colors group-hover:border-primary/40 group-hover:text-seo-bright">
                                <Icon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-foreground/90 transition-colors group-hover:text-foreground">
                                  {service.title}
                                </span>
                                {service.badge && (
                                  <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wider text-primary-foreground">
                                    {service.badge}
                                  </span>
                                )}
                              </span>
                              {service.description && (
                                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                                  {service.description}
                                </span>
                              )}
                            </span>
                            {hasSubItems(service) && (
                              <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-seo-bright" />
                            )}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Columna derecha: ciudades */}
              {menuHasSubItems && (
                <div className="hidden min-h-full border-l border-border/80 bg-background/40 p-3 md:block">
                  {hovered && hasSubItems(hovered) ? (
                    <div className="animate-fade-in">
                      <div className="mb-2 flex items-center gap-2 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-seo-bright">
                        <MapPin className="h-3 w-3" aria-hidden="true" />
                        Ciudades disponibles
                      </div>
                      <ul className="space-y-0.5">
                        {hovered.subItems!.map((sub) => (
                          <li key={sub.path}>
                            <Link
                              to={sub.path}
                              className="group flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors duration-200 hover:bg-primary/10 hover:text-foreground"
                            >
                              <span className="flex items-center gap-2">
                                <MapPin className="h-3 w-3 text-seo-bright/60 transition-colors group-hover:text-seo-bright" />
                                {sub.title}
                              </span>
                              <ChevronRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex items-start gap-2 rounded-lg border border-premium-gold/25 bg-premium-gold/5 p-3">
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-premium-gold" aria-hidden="true" />
                        <p className="text-[11px] leading-snug text-muted-foreground">
                          <span className="font-semibold text-premium-gold">Exclusividad por sector y zona.</span> Solo trabajamos
                          con un cliente por sector y zona.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center px-4 text-center text-xs italic text-muted-foreground">
                      Pasa el cursor sobre un servicio para ver sus ciudades
                    </div>
                  )}
                </div>
              )}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
